/**
 * @file chat-window-state.ts
 * @description Состояние окна «Сообщения» (chat-window-design.md §6): режим,
 * вкладка, отбор, выбранный диалог — чистый редьюсер, разбор сохранённого
 * состояния и раскладка окна по ширине экрана.
 *
 * Состояние живёт в `sessionStorage` своей вкладки браузера под ключом
 * магазина (`commora:chat:v1:<shopId>`): у двух вкладок разных магазинов —
 * свои окна. Поиск и шторка сведений не сохраняются — это «на минуту».
 */

import type { ChatTab } from "../../lib/api-chat";

export interface ChatCustomerFilter {
  id: string;
  name: string;
}

export interface ChatWindowState {
  open: boolean;
  /** Развёрнуто на весь экран (только от 1024; до 1024 окно всегда на весь экран). */
  expanded: boolean;
  tab: ChatTab;
  unassigned: boolean;
  customer: ChatCustomerFilter | null;
  selectedId: string | null;
  search: string;
}

export const INITIAL_CHAT_STATE: ChatWindowState = {
  open: false,
  expanded: false,
  tab: "all",
  unassigned: false,
  customer: null,
  selectedId: null,
  search: "",
};

export type ChatWindowAction =
  | { type: "open"; selectedId?: string | null; customer?: ChatCustomerFilter | null }
  | { type: "close" }
  | { type: "setExpanded"; expanded: boolean }
  | { type: "tab"; tab: ChatTab }
  | { type: "unassigned"; value: boolean }
  | { type: "customer"; customer: ChatCustomerFilter | null }
  | { type: "search"; value: string }
  | { type: "select"; id: string | null }
  | { type: "reset" };

export function chatWindowReducer(state: ChatWindowState, action: ChatWindowAction): ChatWindowState {
  switch (action.type) {
    case "open":
      return {
        ...state,
        open: true,
        selectedId: action.selectedId !== undefined ? action.selectedId : state.selectedId,
        customer: action.customer !== undefined ? action.customer : state.customer,
        // Отбор по клиенту ищет во «Всех», а не в пустом «Архиве».
        tab: action.customer ? "all" : state.tab,
        unassigned: action.customer ? false : state.unassigned,
      };
    case "close":
      return { ...state, open: false };
    case "setExpanded":
      return { ...state, expanded: action.expanded };
    case "tab":
      // «Без ответственного» имеет смысл только во «Всех».
      return { ...state, tab: action.tab, unassigned: action.tab === "all" ? state.unassigned : false };
    case "unassigned":
      return { ...state, unassigned: action.value, tab: action.value ? "all" : state.tab };
    case "customer":
      return { ...state, customer: action.customer };
    case "search":
      return { ...state, search: action.value };
    case "select":
      return { ...state, selectedId: action.id };
    case "reset":
      return INITIAL_CHAT_STATE;
  }
}

const TABS: readonly ChatTab[] = ["all", "mine", "archive"];

/** Что сохраняем: режим, вкладка, отбор, выбранный диалог. */
type StoredChatState = Pick<ChatWindowState, "open" | "expanded" | "tab" | "unassigned" | "customer" | "selectedId">;

export function serializeChatState(state: ChatWindowState): string {
  const stored: StoredChatState = {
    open: state.open,
    expanded: state.expanded,
    tab: state.tab,
    unassigned: state.unassigned,
    customer: state.customer,
    selectedId: state.selectedId,
  };
  return JSON.stringify(stored);
}

/** Разбор сохранённого: мусор и чужая версия — начальное состояние. */
export function parseChatState(raw: string | null): ChatWindowState {
  if (!raw) return INITIAL_CHAT_STATE;
  try {
    const v = JSON.parse(raw) as Partial<Record<keyof StoredChatState, unknown>>;
    const customer = v.customer as Partial<ChatCustomerFilter> | null | undefined;
    return {
      ...INITIAL_CHAT_STATE,
      open: v.open === true,
      expanded: v.expanded === true,
      tab: TABS.includes(v.tab as ChatTab) ? (v.tab as ChatTab) : "all",
      unassigned: v.unassigned === true,
      customer:
        customer && typeof customer.id === "string" && typeof customer.name === "string"
          ? { id: customer.id, name: customer.name }
          : null,
      selectedId: typeof v.selectedId === "string" ? v.selectedId : null,
    };
  } catch {
    return INITIAL_CHAT_STATE;
  }
}

// ─── Раскладка по ширине (§1б–д) ────────────────────────────────────────────

/** Ширина экрана: телефон < 768, планшет < 1024, ноутбук < 1280, широкий. */
export type ChatViewport = "phone" | "tablet" | "desktop" | "wide";

export interface ChatPresentation {
  /** compact — окно 720 в углу; full — весь экран под полосой входа под сотрудником. */
  frame: "compact" | "full";
  /** Зон в окне: 1 (телефон: список или диалог), 2, 3 (со сведениями). */
  zones: 1 | 2 | 3;
  /** Модальное окно с запертым фокусом. */
  modal: boolean;
  /** Есть кнопка «развернуть / уменьшить». */
  canExpand: boolean;
}

export function chatPresentation(viewport: ChatViewport, expanded: boolean): ChatPresentation {
  if (viewport === "phone") return { frame: "full", zones: 1, modal: true, canExpand: false };
  if (viewport === "tablet") return { frame: "full", zones: 2, modal: true, canExpand: false };
  if (!expanded) return { frame: "compact", zones: 2, modal: false, canExpand: true };
  return { frame: "full", zones: viewport === "wide" ? 3 : 2, modal: true, canExpand: true };
}

// ─── Ключи хранилища (§6) ───────────────────────────────────────────────────

const PREFIX = "commora:chat:";

export const chatStorageKeys = {
  state: (shopId: string) => `${PREFIX}v1:${shopId}`,
  draft: (shopId: string, conversationId: string) => `${PREFIX}draft:${shopId}:${conversationId}`,
  /** Владелец ключей вкладки: другой сотрудник в той же вкладке — ключи стираются. */
  owner: () => `${PREFIX}owner`,
  /** «Служебные записи показаны / свёрнуты» — за сотрудником, в localStorage. */
  service: (userId: string) => `${PREFIX}service:${userId}`,
};

/** Стереть ключи чата вкладки (выход, другой сотрудник). */
export function clearChatSession(storage: Storage = window.sessionStorage): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  keys.forEach((key) => storage.removeItem(key));
}
